import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AssetTableComponent } from '../../../asset-table/asset-table.component';
import { HC_COLORS } from 'src/app/charts/highcharts-shared';

@Component({
  selector: 'app-portfolio-selection-table',
  templateUrl: './portfolio-selection-table.component.html',
  styleUrls: ['./portfolio-selection-table.component.scss'],
  standalone: true,
  imports: [CommonModule, AssetTableComponent],
})
export class PortfolioSelectionTableComponent implements OnChanges {
  @Input() list: any[] = [];
  @Input() selectedIds: number[] = [];

  @Output() portfolioSelect = new EventEmitter<{ portfolio: any; isSelected: boolean }>();

  /** Rows mapped into the shape app-asset-table (simpleMode) expects. */
  rows: any[] = [];
  sorting: { [key: string]: 'asc' | 'desc' | null } = { last_updated: 'desc' };

  /** Generic column definitions consumed by app-asset-table in simpleMode. */
  readonly quantList = [
    { id: 'status', title: 'Status', cellType: 'icon', colValue: '_statusCell', sortable: true, sortKey: 'status' },
    { id: 'currency', title: 'Currency', cellType: 'text', colValue: '_currency', sortable: true, sortKey: 'currency' },
    { id: 'security_count', title: 'Securities', cellType: 'number', colValue: 'security_count', sortable: true, sortKey: 'security_count' },
    { id: 'mcap', title: 'MCap (M$)', cellType: 'number', colValue: '_mcap', align: 'end', sortable: true, sortKey: 'mcap' },
    { id: 'weighting_type', title: 'Weighting', cellType: 'text', colValue: '_weighting', sortable: true, sortKey: 'weighting_type' },
    { id: 'last_updated', title: 'Last Updated', cellType: 'date', colValue: '_lastUpdated', align: 'end', sortable: true, sortKey: 'last_updated' },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['list'] || changes['selectedIds']) {
      this.applySort();
    }
  }

  onSortClick(column: string): void {
    const current = this.sorting[column];
    this.sorting = {};
    if (!current) {
      this.sorting[column] = 'asc';
    } else if (current === 'asc') {
      this.sorting[column] = 'desc';
    }
    this.applySort();
  }

  onItemSelect(event: { company: any; isSelected: boolean }): void {
    this.portfolioSelect.emit({ portfolio: event.company, isSelected: event.isSelected });
  }

  private applySort(): void {
    const col = Object.keys(this.sorting).find(k => !!this.sorting[k]);
    let ordered = [...this.list];
    if (col) {
      const dir = this.sorting[col];
      ordered = ordered.sort((a, b) => {
        const va = this.getSortValue(a, col);
        const vb = this.getSortValue(b, col);
        if (va < vb) return dir === 'asc' ? -1 : 1;
        if (va > vb) return dir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    this.rows = ordered.map(p => this.toRow(p));
  }

  private toRow(p: any): any {
    const disabled = this.isRowDisabled(p);
    return {
      ...p,
      company_name: p.name,
      selected: this.selectedIds.includes(p.id),
      disabled,
      disabledTooltip: this.getRowDisabledTooltip(p),
      _statusCell: {
        icon: this.getStatusIcon(p.status),
        color: this.getStatusColor(p.status),
        tooltip: this.getStatusTooltip(p.status),
      },
      _currency: p.currency?.code || p.currency || '-',
      _mcap: this.getMcap(p),
      _weighting: p.weighting_type || '-',
      _lastUpdated: p.status === 3 ? p.last_updated_at ?? p.updated_at : null,
    };
  }

  private getSortValue(item: any, column: string): any {
    switch (column) {
      case 'currency': return (item.currency?.code ?? item.currency ?? '').toString().toLowerCase();
      case 'last_updated': return item.last_updated_at ?? item.created_at ?? '';
      case 'security_count': return Number(item.security_count) || 0;
      case 'status': return Number(item.status) || 0;
      case 'mcap': return Number(this.getMcap(item)) || 0;
      default: return (item[column] ?? '').toString().toLowerCase();
    }
  }

  private getMcap(portfolio: any): number | null {
    const value = portfolio?.performance_summary?.['Market Value']?.Portfolio;
    return value != null ? +value : null;
  }

  private isRowDisabled(portfolio: any): boolean {
    return portfolio.status !== 3 || !!portfolio.sub_portfolios_count;
  }

  private getRowDisabledTooltip(portfolio: any): string {
    if (portfolio.sub_portfolios_count) {
      return 'This portfolio already contains sub-portfolios';
    }
    if (portfolio.status !== 3) {
      return 'Portfolio not yet computed';
    }
    return '';
  }

  private getStatusIcon(status: number): string {
    return status === 3 ? 'check_circle' : 'warning';
  }

  private getStatusColor(status: number): string {
    return status === 3 ? HC_COLORS.pos : HC_COLORS.warn;
  }

  private getStatusTooltip(status: number): string {
    return status === 3 ? 'Computed' : 'Portfolio not yet computed';
  }
}
